import { useState } from 'react';

export interface InviteRecord {
  id: string;
  clientName: string | null;
  clientEmail: string | null;
  deliveryModel?: 'SELF_SERVICE' | 'MANAGED' | string;
  overridePermission?: 'LOCKED' | 'ALLOWED_WITH_REASON' | 'ALLOWED' | string;
  inviteUrl: string;
  createdAt: string;
  expiresAt: string;
  revokedAt?: string | null;
  status: 'PENDING' | 'USED' | 'EXPIRED' | 'REVOKED';
}

interface Props {
  invites: InviteRecord[];
  copiedId: string | null;
  resendingId: string | null;
  onCopy: (url: string, id: string) => void;
  onRevoke: (id: string) => void;
  onResend: (invite: InviteRecord) => void;
}

const badgeStyle = (bg: string, text: string): React.CSSProperties => ({
  backgroundColor: bg,
  color: text,
  fontSize: 12,
  padding: '2px 8px',
  borderRadius: 9999,
  fontWeight: 500,
});

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 500,
  padding: '12px 16px',
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 14,
  color: 'var(--text)',
};

export default function InvitationsPanel({
  invites,
  copiedId,
  resendingId,
  onCopy,
  onRevoke,
  onResend,
}: Props) {
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const pending = invites.filter((i) => i.status === 'PENDING');
  // History includes USED so accepted invites remain auditable from the portal.
  const history = invites.filter(
    (i) => i.status === 'USED' || i.status === 'EXPIRED' || i.status === 'REVOKED',
  );

  // Build a lookup of emails that already have a live PENDING invite.
  // Resend on a history row is suppressed when the same recipient is
  // already in the pending set, because generating a second live token
  // for the same mailbox would leave onboarding state ambiguous about
  // which invite link the client should use. The server also enforces
  // this with a 409, but suppressing the UI button avoids a confusing
  // "Resending…" → error round-trip for the common case.
  const pendingEmails = new Set<string>(
    pending
      .map((i) => i.clientEmail)
      .filter((e): e is string => typeof e === 'string' && e.length > 0)
      .map((e) => e.toLowerCase()),
  );

  // Nothing to show at all: hide the whole panel
  if (invites.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--text)' }}>
        Active Invitations
      </h2>

      {/* Active (PENDING only) section */}
      <div
        className="border rounded-lg overflow-hidden"
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}
      >
        {pending.length === 0 ? (
          <div
            style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}
          >
            No pending invitations.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((inv) => (
                <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={tdStyle}>{inv.clientName || '--'}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                    {inv.clientEmail || '--'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={badgeStyle('rgba(124, 58, 237, 0.12)', '#7c3aed')}>PENDING</span>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                    {new Date(inv.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button
                        onClick={() => onCopy(inv.inviteUrl, inv.id)}
                        style={{
                          border: '1px solid var(--border)',
                          color: copiedId === inv.id ? '#22c55e' : 'var(--text-muted)',
                          backgroundColor: 'var(--surface)',
                          fontSize: 12,
                          padding: '4px 8px',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      >
                        {copiedId === inv.id ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        onClick={() => onRevoke(inv.id)}
                        style={{
                          color: '#ef4444',
                          border: '1px solid rgba(239,68,68,0.3)',
                          backgroundColor: 'transparent',
                          fontSize: 12,
                          padding: '4px 8px',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* History toggle + section */}
      {history.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setHistoryExpanded((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary-light, #7c3aed)',
              fontSize: 13,
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
            aria-expanded={historyExpanded}
          >
            {historyExpanded
              ? 'Hide invitation history'
              : `Show invitation history (${history.length})`}
          </button>

          {historyExpanded && (
            <div
              data-testid="invite-history-section"
              className="border rounded-lg overflow-hidden mt-2"
              style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--card-bg)',
                opacity: 0.75,
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={thStyle}>Client</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Created</th>
                    <th style={thStyle}>Closed</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((inv) => {
                    const statusBadge =
                      inv.status === 'USED' ? (
                        <span style={badgeStyle('rgba(34,197,94,0.1)', '#15803d')}>ACCEPTED</span>
                      ) : inv.status === 'REVOKED' ? (
                        <span style={badgeStyle('rgba(239,68,68,0.08)', '#b91c1c')}>REVOKED</span>
                      ) : (
                        <span style={badgeStyle('rgba(107,114,128,0.15)', '#6b7280')}>EXPIRED</span>
                      );
                    const eventDate =
                      inv.status === 'REVOKED' ? inv.revokedAt : inv.expiresAt;
                    // Resend is only meaningful when:
                    //  - the row is EXPIRED (USED/REVOKED rows are terminal),
                    //  - we have an email to send to (partners sometimes
                    //    generate link-only invites; the backend silently
                    //    skips mail in that case, so a Resend button would
                    //    wrongly claim "Invitation resent to …"),
                    //  - AND no active PENDING invite already exists for
                    //    the same recipient. See the pendingEmails comment
                    //    above — without this check, Resend would POST a
                    //    second live token and get rejected with a 409.
                    const canResend =
                      inv.status === 'EXPIRED' &&
                      !!inv.clientEmail &&
                      !pendingEmails.has(inv.clientEmail.toLowerCase());
                    return (
                      <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {inv.clientName || '--'}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {inv.clientEmail || '--'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>{statusBadge}</td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {new Date(inv.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {inv.status === 'USED'
                            ? 'Accepted'
                            : eventDate
                              ? new Date(eventDate).toLocaleDateString()
                              : '--'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          {canResend && (
                            <button
                              onClick={() => onResend(inv)}
                              disabled={resendingId === inv.id}
                              style={{
                                border: '1px solid var(--border)',
                                color: 'var(--text)',
                                backgroundColor: 'var(--surface)',
                                fontSize: 12,
                                padding: '4px 10px',
                                borderRadius: 4,
                                cursor: resendingId === inv.id ? 'wait' : 'pointer',
                                opacity: resendingId === inv.id ? 0.6 : 1,
                              }}
                            >
                              {resendingId === inv.id ? 'Resending…' : 'Resend →'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
